import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { GitCompareArrows } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface VideoDuelEmptyStateProps {
  onStart?: () => void;
}

export function VideoDuelEmptyState({ onStart }: VideoDuelEmptyStateProps) {
  useLanguage();
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-200px)] text-center px-4">
      <div className="mb-8 flex justify-center">
        <div className="rounded-full bg-blue-100 p-6">
          <div className="w-16 h-16 rounded-full bg-blue-500 flex items-center justify-center">
            <GitCompareArrows size={32} className="text-white" />
          </div>
        </div>
      </div>

      <h2 className="text-lg sm:text-xl md:text-2xl font-semibold mb-3 max-w-4xl mx-auto px-2">
        {translateInterfaceText("Compare suas VSLs lado a lado e descubra qual performa melhor")}</h2>
      <p className="text-sm md:text-base text-muted-foreground mb-8 max-w-md mx-auto leading-relaxed">
        {translateInterfaceText("Analise retenção, plays, conversão e desempenho em uma visão comparativa completa com dados reais")}</p>

      <div className="flex flex-col sm:flex-row gap-4">
        <Button
          onClick={onStart}
          className="flex items-center justify-center gap-2 min-h-[44px] px-6"
        >
          {translateInterfaceText("Comparar agora")}</Button>

        <Button
          variant="outline"
          onClick={() => setShowHowItWorks(true)}
          className="px-6 min-h-[44px] hover:bg-transparent hover:text-[hsl(var(--foreground))]"
        >
          {translateInterfaceText("Como funciona?")}</Button>
      </div>

      <Dialog open={showHowItWorks} onOpenChange={setShowHowItWorks}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{translateInterfaceText("Como funciona a comparação de VSLs")}</DialogTitle>
            <DialogDescription className="pt-4">
              <div className="space-y-4">
                <div className="border-l-4 border-blue-500 pl-4 py-2">
                  <h3 className="font-medium mb-1">{translateInterfaceText("1. Selecione duas VSLs")}</h3>
                  <p className="text-sm text-gray-600">
                    {translateInterfaceText("Escolha os vídeos que deseja comparar lado a lado.")}</p>
                </div>

                <div className="border-l-4 border-blue-500 pl-4 py-2">
                  <h3 className="font-medium mb-1">{translateInterfaceText("2. Analise os dados reais")}</h3>
                  <p className="text-sm text-gray-600">
                    {translateInterfaceText("Veja plays, retenção, conversão e outros indicadores importantes.")}</p>
                </div>

                <div className="border-l-4 border-blue-500 pl-4 py-2">
                  <h3 className="font-medium mb-1">{translateInterfaceText("3. Identifique a vencedora")}</h3>
                  <p className="text-sm text-gray-600">
                    {translateInterfaceText("Entenda qual VSL performa melhor com base em métricas claras.")}</p>
                </div>

                <div className="border-l-4 border-blue-500 pl-4 py-2">
                  <h3 className="font-medium mb-1">{translateInterfaceText("4. Otimize suas decisões")}</h3>
                  <p className="text-sm text-gray-600">
                    {translateInterfaceText("Use os resultados para melhorar criativos, páginas e escala.")}</p>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  );
}
